import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api';
import { GameId, GameCategory } from '../../types/enums';
import { GameStats, GameAnalytics } from '../../components/page-components/Dashboard/types';

// Games state interface
interface GamesState {
  stats: GameStats;
  analytics: GameAnalytics | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: GamesState = {
  stats: {
    gamesPlayed: 0,
    totalTime: 0,
    badgesEarned: 0,
  },
  analytics: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchGameStats = createAsyncThunk(
  'games/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      // Use analytics API to get game stats
      const analytics = await api.games.getAnalytics();
      // Map analytics values to GameStats
      return {
        gamesPlayed: analytics.totalGames,
        totalTime: analytics.totalDuration,
        badgesEarned: 0, // Badges might need a separate endpoint or calculation
      } as GameStats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch game stats'
      );
    }
  }
);

export const updateGameStats = createAsyncThunk(
  'games/updateStats',
  async (stats: Partial<GameStats>, { rejectWithValue }) => {
    try {
      const updatedStats = await api.games.updateStats(stats);
      return updatedStats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update game stats'
      );
    }
  }
);

export const createGame = createAsyncThunk(
  'games/createGame',
  async (gameData: {
    gameId: GameId;
    gameName: string;
    category: GameCategory;
    emoji: string;
    description: string;
    duration: number;
    score: number;
    completed: boolean;
  }, { rejectWithValue }) => {
    try {
      const game = await api.games.createGame(gameData);
      return game;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create game record'
      );
    }
  }
);

export const fetchGameAnalytics = createAsyncThunk(
  'games/fetchAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const analytics = await api.games.getAnalytics();
      return analytics;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch game analytics'
      );
    }
  }
);

// Games slice
const gamesSlice = createSlice({
  name: 'games',
  initialState,
  reducers: {
    setStats: (state, action: PayloadAction<GameStats>) => {
      state.stats = action.payload;
    },
    incrementGamesPlayed: (state) => {
      state.stats.gamesPlayed += 1;
    },
    addTime: (state, action: PayloadAction<number>) => {
      // Convert seconds to minutes and round to 2 decimal places
      const timeInMinutes = action.payload / 60;
      const newTotalTime = state.stats.totalTime + timeInMinutes;
      state.stats.totalTime = parseFloat(newTotalTime.toFixed(2));
    },
    incrementBadgesEarned: (state) => {
      state.stats.badgesEarned += 1;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch game stats
    builder
      .addCase(fetchGameStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGameStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
        // Also update analytics if available
        if (state.analytics) {
          state.analytics = {
            ...state.analytics,
            totalGames: action.payload.gamesPlayed,
            totalDuration: action.payload.totalTime,
          };
        }
      })
      .addCase(fetchGameStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update game stats
    builder
      .addCase(updateGameStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateGameStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(updateGameStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create game
    builder
      .addCase(createGame.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createGame.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createGame.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch game analytics
    builder
      .addCase(fetchGameAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchGameAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload;
        // Also update stats from analytics
        state.stats = {
          gamesPlayed: action.payload.totalGames,
          totalTime: action.payload.totalDuration,
          badgesEarned: state.stats.badgesEarned, // Keep existing badges count
        };
      })
      .addCase(fetchGameAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setStats,
  incrementGamesPlayed,
  addTime,
  incrementBadgesEarned,
  setError,
  clearError,
} = gamesSlice.actions;
export default gamesSlice.reducer;

